import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ShieldAlert, CheckCircle } from 'lucide-react';

const AccidentsTab = ({
  accidentReports,
  loadingReports,
  handleAcknowledgeReport
}) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-semibold text-white flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-red-400" />
          Accident Reports
        </h3>
      </div>

      {loadingReports ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full" />
        </div>
      ) : accidentReports.length === 0 ? (
        <Card className="bg-[#111] border-[#333]">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h3 className="font-heading text-lg font-semibold mb-2 text-white">No Accident Reports</h3>
            <p className="text-white/50">Great news! There are no accident reports for your children.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {accidentReports.map(report => (
            <Card key={report.id} className={`bg-[#111] border-[#333] ${report.acknowledgment_requested && !report.acknowledged ? 'border-l-4 border-l-orange-500' : ''}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-lg font-semibold text-white">{report.student_name}</h4>
                      {report.acknowledged ? (
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Acknowledged</Badge>
                      ) : report.acknowledgment_requested ? (
                        <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">Action Required</Badge>
                      ) : (
                        <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Pending Review</Badge>
                      )}
                    </div>
                    <p className="text-sm text-white/50">{new Date(report.date_time).toLocaleString()}</p>
                  </div>
                  {report.requires_medical && (
                    <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Medical Attention Required</Badge>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div className="p-3 bg-[#1a1a1a] rounded-lg">
                    <p className="text-white/50 text-xs mb-1">Location</p>
                    <p className="text-white">{report.location}</p>
                  </div>
                  <div className="p-3 bg-[#1a1a1a] rounded-lg">
                    <p className="text-white/50 text-xs mb-1">Reported By</p>
                    <p className="text-white">{report.reported_by_name}</p>
                  </div>
                </div>

                <div className="p-3 bg-[#1a1a1a] rounded-lg mb-4">
                  <p className="text-white/50 text-xs mb-1">Description</p>
                  <p className="text-white">{report.description}</p>
                </div>

                {report.injuries && (
                  <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/20 mb-4">
                    <p className="text-red-400 text-xs mb-1">Injuries</p>
                    <p className="text-white">{report.injuries}</p>
                  </div>
                )}

                {report.treatment && (
                  <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/20 mb-4">
                    <p className="text-green-400 text-xs mb-1">Treatment Given</p>
                    <p className="text-white">{report.treatment}</p>
                  </div>
                )}

                {report.acknowledgment_requested && !report.acknowledged && (
                  <div className="p-4 bg-orange-500/10 rounded-lg border border-orange-500/30">
                    <p className="text-orange-400 text-sm mb-3">
                      Please acknowledge this report when collecting your child.
                    </p>
                    <Button
                      onClick={() => handleAcknowledgeReport(report.id)}
                      className="bg-orange-500 hover:bg-orange-600"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Acknowledge Report
                    </Button>
                  </div>
                )}

                {report.acknowledged && (
                  <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                    <p className="text-green-400 text-sm">
                      <CheckCircle className="w-4 h-4 inline mr-1" />
                      Acknowledged by {report.acknowledged_by_name} on {new Date(report.acknowledged_at).toLocaleString()}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AccidentsTab;
